import * as React from 'react';
import { Id } from '../../utils/ids.js';
import { addClass, Bem } from '../../utils/css.js';
import { Placement } from '../../constants.js';
import { useFloatingReference, useTriggerElement } from '../../utils/floating-ui.js';
import { useFloating, autoUpdate, offset, flip, shift, arrow, useClick, useHover, useFocus, useDismiss, useRole, useInteractions, FloatingPortal } from '@floating-ui/react';
import './Tooltip.scss.js';

const bem = Bem.create("vai-tooltip");
const TooltipArrow = React.forwardRef(function TooltipArrow2(props, ref) {
  const { dataTa = "vai-tooltip-arrow", visible, className, ...rest } = props;
  const tooltipArrowClasses = addClass(bem.element("arrow")(), className);
  return visible ? /* @__PURE__ */ React.createElement("div", {
    ref,
    "data-ta": dataTa,
    className: tooltipArrowClasses,
    ...rest
  }) : null;
});
function Tooltip(props) {
  const {
    triggerRef,
    triggerElement,
    trigger = "hover",
    fixed = false,
    placement = Placement.Bottom,
    arrow: hasArrow = false,
    visible,
    onClose,
    children,
    htmlId,
    dataTa,
    className,
    delay = { open: 100, close: 200 },
    virtualElement
  } = props;
  const id = Id.create(htmlId, ["tooltip"]);
  const [open, setRawOpen] = React.useState(false);
  const setOpen = (value) => {
    if (value === false && onClose instanceof Function) {
      onClose();
    }
    if (visible !== void 0) {
      return;
    }
    setRawOpen(value);
  };
  React.useEffect(() => {
    setRawOpen(visible);
  }, [visible]);
  const arrowRef = React.useRef(null);
  const {
    context,
    x,
    y,
    refs,
    strategy,
    update,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
    placement: calculatedPlacement
  } = useFloating({
    strategy: fixed ? "fixed" : "absolute",
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted(...args) {
      const cleanup = autoUpdate(...args, {
        elementResize: typeof ResizeObserver === "function"
      });
      return cleanup;
    },
    middleware: [offset({ mainAxis: 8 }), flip(), shift(), arrow({ element: arrowRef })]
  });
  const arrowCallback = React.useCallback((node) => {
    arrowRef.current = node;
    update();
  }, [update]);
  const click = useClick(context, { enabled: trigger === "click" });
  const hover = useHover(context, { enabled: trigger === "hover", move: false, delay });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, focus, dismiss, role]);
  useFloatingReference(refs.setReference, triggerRef?.current, getReferenceProps);
  useFloatingReference(refs.setReference, virtualElement);
  const triggerEl = useTriggerElement(refs.setReference, triggerElement, getReferenceProps);
  const tooltipClass = addClass(bem.block(), className);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, triggerEl, /* @__PURE__ */ React.createElement(FloatingPortal, null, open && /* @__PURE__ */ React.createElement("div", {
    id: id.toString(),
    "data-ta": dataTa,
    "data-placement": calculatedPlacement,
    className: tooltipClass,
    ref: refs.setFloating,
    style: { position: strategy, top: y ?? 0, left: x ?? 0 },
    ...getFloatingProps()
  }, children, /* @__PURE__ */ React.createElement(TooltipArrow, {
    visible: hasArrow,
    ref: arrowCallback,
    style: {
      left: arrowX ?? "",
      top: arrowY ?? ""
    }
  }))));
}

export { Tooltip };
